import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { generateUniqueId } from "./bookingUtils";
import { normalizePhone } from "@/lib/phone-utils";

export interface BookingData {
  businessId: string;
  serviceId: string;
  clientId?: string;              // Now optional - will be created async if needed
  providerId?: string;
  bookingDate: Date;
  bookingTime: string;
  duration: number;
  specialRequests?: string;
  paymentId?: string;
  paymentIntentId?: string;
  // Customer snapshot fields (required if clientId not provided)
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
}

export interface ClientData {
  id?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  businessId?: string; // Legacy: The business_profiles.id (will fetch user_id from it)
  businessUserId?: string; // Preferred: The business owner's user_id directly
}

export interface ExistingBooking {
  id: string;
  booking_date: string;
  booking_time: string;
  duration: number;
  status: string;
}

/**
 * Generate a unique confirmation number that doesn't exist in the database
 */
export async function generateUniqueConfirmationNumber(): Promise<string> {
  const maxAttempts = 10;
  let attempts = 0;
  
  while (attempts < maxAttempts) {
    const confirmationNumber = generateUniqueId();
    
    // Check if this confirmation number already exists
    const { data: existingBooking, error } = await supabase
      .from('bookings')
      .select('id')
      .eq('confirmation_number', confirmationNumber)
      .maybeSingle();
    
    if (error && error.code !== 'PGRST116') {
      console.error('Error checking confirmation number uniqueness:', error);
      throw new Error('Failed to generate unique confirmation number');
    }
    
    // If no existing booking found, this confirmation number is unique
    if (!existingBooking) {
      return confirmationNumber;
    }
    
    attempts++;
  }
  
  throw new Error('Failed to generate unique confirmation number after multiple attempts');
}

/**
 * Check if a time slot is available for booking
 */
export async function checkTimeSlotAvailability(
  businessId: string,
  serviceId: string,
  date: Date,
  time: string,
  duration: number
): Promise<{ available: boolean; conflictingBookings?: ExistingBooking[] }> {
  try {
    const formattedDate = format(date, 'yyyy-MM-dd');
    
    // Get existing bookings for this business on this date
    const { data: existingBookings, error } = await supabase
      .from('bookings')
      .select('id, booking_date, booking_time, duration, status')
      .eq('business_id', businessId)
      .eq('booking_date', formattedDate)
      .in('status', ['confirmed', 'completed']) // Only check confirmed and completed bookings
      .order('booking_time');

    if (error) {
      console.error('Error checking availability:', error);
      return { available: false };
    }

    if (!existingBookings || existingBookings.length === 0) {
      return { available: true };
    }

    // Convert time strings to minutes for easier comparison
    const timeToMinutes = (timeStr: string): number => {
      const [time, period] = timeStr.split(' ');
      const [hours, minutes] = time.split(':').map(Number);
      let totalMinutes = hours * 60 + minutes;
      
      if (period === 'PM' && hours !== 12) {
        totalMinutes += 12 * 60;
      } else if (period === 'AM' && hours === 12) {
        totalMinutes -= 12 * 60;
      }
      
      return totalMinutes;
    };

    const requestedStartTime = timeToMinutes(time);
    const requestedEndTime = requestedStartTime + duration;

    // Check for conflicts
    const conflictingBookings = existingBookings.filter(booking => {
      const existingStartTime = timeToMinutes(convertDbTimeToDisplayTime(booking.booking_time));
      const existingEndTime = existingStartTime + booking.duration;

      // Check if times overlap
      return (
        (requestedStartTime < existingEndTime && requestedEndTime > existingStartTime)
      );
    });

    return {
      available: conflictingBookings.length === 0,
      conflictingBookings: conflictingBookings.length > 0 ? conflictingBookings : undefined
    };

  } catch (error) {
    console.error('Error checking time slot availability:', error);
    return { available: false };
  }
}

/**
 * Convert database time format to display format
 */
function convertDbTimeToDisplayTime(dbTime: string): string {
  // Database stores time as HH:MM:SS, convert to display format like "2:30 PM"
  const [hours, minutes] = dbTime.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  const displayMinutes = minutes.toString().padStart(2, '0');
  
  return `${displayHours}:${displayMinutes} ${period}`;
}

/**
 * Convert display time format to database format
 */
function convertDisplayTimeToDbTime(displayTime: string): string {
  const [time, period] = displayTime.split(' ');
  const [hours, minutes] = time.split(':').map(Number);
  
  let dbHours = hours;
  if (period === 'PM' && hours !== 12) {
    dbHours += 12;
  } else if (period === 'AM' && hours === 12) {
    dbHours = 0;
  }
  
  return `${dbHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
}

/**
 * Find existing client by phone or create new one (CRM MVP - phone-first pattern)
 */
export async function findOrCreateClient(clientData: ClientData): Promise<{ success: boolean; clientId?: string; error?: string }> {
  try {
    // Determine user_id: use businessUserId if provided, otherwise fetch from businessId
    let userId = clientData.businessUserId;

    if (!userId && clientData.businessId) {
      // Legacy path: fetch user_id from business_profiles
      const { data: business, error: businessError } = await supabase
        .from('business_profiles')
        .select('user_id')
        .eq('id', clientData.businessId)
        .single();

      if (businessError || !business) {
        console.error('Error fetching business user_id:', businessError);
        return { success: false, error: 'Business not found' };
      }

      userId = business.user_id;
    }

    if (!userId) {
      return { success: false, error: 'Business user ID is required' };
    }

    // Normalize phone to E.164 format
    const phone = clientData.phone ? normalizePhone(clientData.phone) : null;

    // Use phone-first upsert when phone is available; fall back to email upsert
    const upsertPayload: Record<string, unknown> = {
      email: clientData.email || null,
      first_name: clientData.firstName || null,
      last_name: clientData.lastName || null,
      user_id: userId,
      sms_allowed: true // implicit via booking
    };
    if (phone) {
      upsertPayload.phone = phone;
    }
    // Marketing consent is intentionally not accepted from this anonymous browser write.
    // A trusted server endpoint must verify proof and record consent evidence before
    // setting sms_marketing_opt_in=true.

    const onConflict = phone ? 'user_id,phone' : 'business_id,email';

    const { data: client, error: upsertError } = await supabase
      .from('clients')
      .upsert(upsertPayload, { onConflict })
      .select('id')
      .single();

    if (upsertError) {
      console.error('Error upserting client:', upsertError);
      return { success: false, error: upsertError.message };
    }

    return { success: true, clientId: client.id };

  } catch (error) {
    console.error('Error in findOrCreateClient:', error);
    return { success: false, error: 'Failed to process client information' };
  }
}

/**
 * Create a new booking in the database
 *
 * BOOK-06 Fix: This function is protected against double-booking race conditions
 * by a database trigger that checks for overlapping bookings at the database level.
 * The trigger will throw a 23505 error if a conflict is detected, which we handle
 * gracefully with a user-friendly error message.
 *
 * PUBLIC BOOKING SUPPORT: RLS policy allows anon users to create bookings with:
 * - Required customer snapshot (name + email)
 * - Service must belong to business (prevents cross-business attacks)
 * - payment_intent_id is optional — pay-in-person bookings send NULL and are allowed
 */
export async function createBooking(bookingData: BookingData): Promise<{ success: boolean; bookingId?: string; confirmationNumber?: string; error?: string }> {
  try {
    // First check availability (defense in depth - database trigger is the final authority)
    const availabilityCheck = await checkTimeSlotAvailability(
      bookingData.businessId,
      bookingData.serviceId,
      bookingData.bookingDate,
      bookingData.bookingTime,
      bookingData.duration
    );

    if (!availabilityCheck.available) {
      return {
        success: false,
        error: 'Time slot is no longer available. Please choose a different time.'
      };
    }

    // Generate a unique confirmation number
    const uniqueConfirmationNumber = await generateUniqueConfirmationNumber();

    // Validate: either client_id OR customer contact info must be provided
    if (!bookingData.clientId && (!bookingData.customerName || !bookingData.customerEmail)) {
      return {
        success: false,
        error: 'Either client ID or customer contact information (name + email) is required'
      };
    }

    // Direct INSERT with RLS policy allowing public bookings
    // Policy validates: customer snapshot, payment_intent_id, business/service relationship
    const insertData = {
      business_id: bookingData.businessId,
      service_id: bookingData.serviceId,
      client_id: bookingData.clientId || null,
      customer_name: bookingData.customerName,
      customer_email: bookingData.customerEmail,
      customer_phone: bookingData.customerPhone,
      provider_id: bookingData.providerId,
      booking_date: format(bookingData.bookingDate, 'yyyy-MM-dd'),
      booking_time: convertDisplayTimeToDbTime(bookingData.bookingTime),
      duration: bookingData.duration,
      confirmation_number: uniqueConfirmationNumber,
      special_requests: bookingData.specialRequests,
      payment_id: bookingData.paymentId,
      payment_intent_id: bookingData.paymentIntentId,
      status: 'confirmed',
      created_at: new Date().toISOString()
    };

    console.log('Attempting booking insert with data:', {
      has_customer_name: !!insertData.customer_name,
      has_customer_email: !!insertData.customer_email,
      customer_email: insertData.customer_email,
      has_payment_intent: !!insertData.payment_intent_id,
      payment_intent_format: insertData.payment_intent_id?.startsWith('pi_'),
      booking_date: insertData.booking_date,
      booking_time: insertData.booking_time,
      duration: insertData.duration,
      status: insertData.status,
      business_id: insertData.business_id,
      service_id: insertData.service_id
    });

    // Use insert without .select() to avoid the RETURNING clause.
    // PostgREST's INSERT ... RETURNING requires the new row to pass SELECT RLS.
    // Anon users have no SELECT policy that covers newly-created bookings without
    // a Stripe payment_intent_id, so .select('id').single() fails with 42501.
    // Instead, insert with return=minimal, then look up the booking by its
    // pre-generated confirmation_number (which is unique and already in insertData).
    const { error } = await supabase
      .from('bookings')
      .insert(insertData);

    if (error) {
      console.error('Error creating booking:', error);

      // Handle double booking race condition (BOOK-06 fix)
      // Error code 23505 is thrown by our database trigger when bookings overlap
      if (error.code === '23505' || error.message.includes('already booked')) {
        return {
          success: false,
          error: 'This time slot was just booked by someone else. Please choose a different time.'
        };
      }

      // Handle availability validation trigger (enforce_booking_availability)
      // Error code P0001 is raised when booking fails date override, blackout,
      // business hours, or cutoff validation at the database level
      if (error.code === 'P0001') {
        return {
          success: false,
          error: error.message || 'This time slot is not available. Please choose a different time.'
        };
      }

      return { success: false, error: error.message };
    }

    // Retrieve the booking ID via a narrow SECURITY DEFINER RPC.
    // Direct SELECT is avoided because the SELECT RLS policy for anon users is
    // restrictive — using an RPC that runs as postgres bypasses RLS and returns
    // only the UUID (no PII exposed).
    const { data: bookingId, error: fetchError } = await supabase
      .rpc('get_booking_id_by_confirmation', { p_confirmation_number: uniqueConfirmationNumber });

    if (fetchError || !bookingId) {
      // Insert succeeded but we can't retrieve the ID — return success with the
      // confirmation number so the UI can still show confirmation.
      console.warn('Booking inserted but could not fetch id by confirmation_number:', fetchError);
      return {
        success: true,
        confirmationNumber: uniqueConfirmationNumber
      };
    }

    const newBooking = { id: bookingId as string };

    // Send admin notification and email confirmation for new booking
    try {
      const { data: service } = await supabase
        .from('business_services')
        .select('name')
        .eq('id', bookingData.serviceId)
        .single();

      const { data: business } = await supabase
        .from('business_profiles')
        .select('name, phone, email')
        .eq('id', bookingData.businessId)
        .single();

      // Get customer info: prefer client record if exists, fallback to customer snapshot
      let customerName: string | null = null;
      let customerEmail: string | null = null;

      if (bookingData.clientId) {
        // Try to get from client record
        const { data: client } = await supabase
          .from('clients')
          .select('first_name, last_name, email')
          .eq('id', bookingData.clientId)
          .single();

        if (client) {
          customerName = `${client.first_name} ${client.last_name}`;
          customerEmail = client.email;
        }
      }

      // Fallback to customer snapshot if no client record
      if (!customerName) {
        customerName = bookingData.customerName || null;
        customerEmail = bookingData.customerEmail || null;
      }

      if (service && business && customerName) {
        // Send admin SMS notification (fire-and-forget — never block booking creation)
        supabase.functions.invoke('notify-admin-booking', {
          body: { booking_id: newBooking.id, event_type: 'new_booking' },
        }).catch((err: unknown) => {
          console.error('[notify-admin-booking] invoke failed (non-fatal):', err);
        });

        // Send client email confirmation (retry once on transient failure)
        if (customerEmail) {
          let emailSent = false;
          for (let attempt = 1; attempt <= 2 && !emailSent; attempt++) {
            try {
              const { error: emailInvokeError } = await supabase.functions.invoke('booking-confirmation', {
                body: { booking_id: newBooking.id }
              });
              if (!emailInvokeError) {
                emailSent = true;
              } else {
                console.error(`[booking-confirmation] Attempt ${attempt} error (non-fatal):`, emailInvokeError.message);
              }
            } catch (emailError) {
              console.error(`[booking-confirmation] Attempt ${attempt} threw (non-fatal):`, emailError);
            }
          }
          if (!emailSent) {
            // Log with booking ID so ops can manually trigger a re-send
            console.error(`[booking-confirmation] FAILED after 2 attempts for booking ${newBooking.id} — manual re-send required`);
          }
        }
      }
    } catch (notificationError) {
      console.error('Failed to send notifications:', notificationError);
      // Don't fail the booking if notifications fail
    }

    return { success: true, bookingId: newBooking.id, confirmationNumber: uniqueConfirmationNumber };

  } catch (error) {
    console.error('Error in createBooking:', error);
    return { success: false, error: 'Failed to create booking' };
  }
}

/**
 * Get all bookings for a specific date and business (for availability checking)
 */
export async function getBookingsForDate(businessId: string, date: Date): Promise<{ success: boolean; bookings?: ExistingBooking[]; error?: string }> {
  try {
    const formattedDate = format(date, 'yyyy-MM-dd');
    
    const { data: bookings, error } = await supabase
      .from('bookings')
      .select('id, booking_date, booking_time, duration, status')
      .eq('business_id', businessId)
      .eq('booking_date', formattedDate)
      .in('status', ['confirmed', 'completed'])
      .order('booking_time');

    if (error) {
      console.error('Error getting bookings for date:', error);
      return { success: false, error: error.message };
    }

    return { success: true, bookings: bookings || [] };

  } catch (error) {
    console.error('Error in getBookingsForDate:', error);
    return { success: false, error: 'Failed to get bookings' };
  }
}